/**
 * Utility functions for Crisp chat widget integration
 * Handles initialization and data population for both browser and mobile app environments
 */

/**
 * Ensure the Crisp command queue exists so we can push commands.
 * In Capacitor/native WebView the app may run before index.html scripts, so create the queue if missing.
 * Only creates an array when $crisp is missing (do not overwrite the SDK if it has already replaced the queue).
 * @returns {boolean} - True if we have a queue (existing or created) to push to
 */
function ensureCrispQueue() {
  if (typeof window === 'undefined') return false;
  if (!window.$crisp) {
    window.$crisp = [];
  }
  return Array.isArray(window.$crisp) || typeof window.$crisp?.push === 'function';
}

/**
 * Wait for Crisp to be initialized (array exists), or ensure the queue so we can push.
 * Crisp commands can be pushed to the array even before Crisp is fully loaded.
 * @param {number} maxRetries - Maximum number of retries (default: 20)
 * @param {number} retryDelay - Delay between retries in ms (default: 250)
 * @returns {Promise<boolean>} - Returns true if we have a queue to push to
 */
export function waitForCrispReady(maxRetries = 20, retryDelay = 250) {
  return new Promise(resolve => {
    if (ensureCrispQueue()) {
      resolve(true);
      return;
    }

    let retries = 0;
    const checkInterval = setInterval(() => {
      retries++;
      if (ensureCrispQueue()) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (retries >= maxRetries) {
        clearInterval(checkInterval);
        resolve(false);
      }
    }, retryDelay);
  });
}

/**
 * Set Crisp user data (email and nickname)
 * @param {Object} data - User data object
 * @param {string|Object} data.email - User email (string or object with email property)
 * @param {string} data.nickname - User nickname (preferredName or givenName)
 * @returns {Promise<boolean>} - Returns true if data was set successfully
 */
export async function setCrispUserData({ email, nickname }) {
  try {
    const isReady = await waitForCrispReady();
    if (!isReady || typeof window === 'undefined' || !window.$crisp) return false;

    const emailValue = typeof email === 'string' ? email : email?.email;
    if (emailValue) window.$crisp.push(['set', 'user:email', [emailValue]]);
    if (nickname) window.$crisp.push(['set', 'user:nickname', [nickname]]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Set Crisp session data (member ID, CG name, role, etc.)
 * @param {Object} data - Session data object
 * @param {number} data.id - Member ID
 * @param {string} data.cgName - CG name
 * @param {string} data.role - Member role
 * @returns {Promise<boolean>} - Returns true if data was set successfully
 */
export async function setCrispSessionData({ id, cgName, role }) {
  try {
    const isReady = await waitForCrispReady();
    if (!isReady || typeof window === 'undefined' || !window.$crisp) return false;

    const sessionPairs = [];
    if (id != null && id !== '') sessionPairs.push(['id', String(id)]);
    if (cgName) sessionPairs.push(['cg', String(cgName)]);
    if (role) sessionPairs.push(['role', String(role)]);
    if (sessionPairs.length) window.$crisp.push(['set', 'session:data', [sessionPairs]]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Set all Crisp metadata (user data + session data) in one call
 * @param {Object} data - Profile data object
 * @param {string|Object} data.email - User email
 * @param {string} data.preferredName - Preferred name
 * @param {string} data.givenName - Given name
 * @param {number} data.id - Member ID
 * @param {string} data.cgName - CG name
 * @param {string} data.role - Member role
 * @returns {Promise<boolean>} - Returns true if all data was set successfully
 */
export async function setCrispMetadata(data) {
  try {
    const isReady = await waitForCrispReady();
    if (!isReady) return false;

    await setCrispUserData({
      email: data.email,
      nickname: data.preferredName || data.givenName,
    });
    await setCrispSessionData({
      id: data.id,
      cgName: data.cgName,
      role: data.role,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Open Crisp chat widget (browser and mobile)
 * @param {Object} options - Options for opening chat
 * @param {boolean} options.showFirst - Whether to show chat before opening (default: false)
 * @param {Object} [options.profileData] - Profile data to set in Crisp before opening (re-syncs user/session data)
 * @returns {Promise<boolean>} - Returns true if command was queued successfully
 */
export async function openCrispChat(options = {}) {
  const { showFirst = false, profileData } = options;

  try {
    if (profileData) await setCrispMetadata(profileData);

    const isReady = await waitForCrispReady();
    if (!isReady || typeof window === 'undefined' || !window.$crisp) return false;

    if (showFirst) window.$crisp.push(['do', 'chat:show']);
    window.$crisp.push(['do', 'chat:open']);
    return true;
  } catch {
    return false;
  }
}
